/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, computed, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {IoMatrixWorkflowComponent} from '../io-matrix-workflow.component';
import {Observable} from 'rxjs';
import {IcdRefMapStatus} from '../../../model/io-matrix.models';
import {AggregatorAllowedProgram, CreateIcdFormOptionsResponse, IcdSelectOption} from '../../../service/esci.service';
import {AggregatorIoMatrixInventory} from '../../../model/aggregator-io-matrix.models';

type SelectOption = { label: string; value: string };

/**
 * A starred field on the metadata form: the metadata key, the label the form shows for it, and whether
 * it only applies to the aggregator flavour.
 */
type RequiredMetadataField = { field: string; label: string; aggregatorOnly?: boolean };

@Component({
  selector: 'app-create-icds',
  templateUrl: './create-icds.component.html',
  styleUrls: ['./create-icds.component.scss', '../io-matrix-workflow.component.scss'],
  standalone: false
})
export class CreateIcdsComponent extends IoMatrixWorkflowComponent {
  private readonly refMapValidationCheckedSignal = signal(false);
  private readonly refMapAvailableSignal = signal(true);
  createIcdErrorVisible = false;
  createIcdErrorMessage = '';

  readonly icdTypeOptions: SelectOption[] = [
    {label: 'Vehicle', value: 'Vehicle'},
    {label: 'GPS Controller', value: 'GPS Controller'},
    {label: 'GPS Device', value: 'GPS Device'},
    {label: 'GPS Jumper', value: 'GPS Jumper'}
  ];

  readonly icdSubTypeOptions: SelectOption[] = [
    {label: 'NA', value: 'NA'},
    {label: 'Diesel', value: 'Diesel'},
    {label: 'Gas', value: 'Gas'},
    {label: 'Hybrid', value: 'Hybrid'},
    {label: 'Transmission', value: 'Transmission'},
    {label: 'Fuel Cell', value: 'Fuel Cell'},
    {label: 'PCM', value: 'PCM'},
    {label: 'Chassis', value: 'Chassis'}
  ];

  revisionOptions: SelectOption[] = [];
  deviceVariantOptions: SelectOption[] = [];
  buildStatusOptions: SelectOption[] = [];
  securityClassifierOptions: SelectOption[] = [{label: 'PROTECTED', value: 'PROTECTED'}];
  componentSourcedOptions: SelectOption[] = [
    {label: 'NO', value: 'N'},
    {label: 'YES', value: 'Y'},
    {label: 'CARRY OVER', value: 'C'}
  ];
  operateNormallyOptions: SelectOption[] = [
    {label: 'No', value: 'N'},
    {label: 'Yes (Requires Power information for Auto Start/Stop on the power tab)', value: 'Y'},
    {label: 'Not Applicable', value: 'NA'}
  ];
  fullyFunctionalOptions: SelectOption[] = [
    {label: 'No (Selecting no will require Auto Start/Stop owner (Del. DRE) to approve)', value: 'N'},
    {label: 'Yes', value: 'Y'},
    {label: 'Not Applicable', value: 'NA'}
  ];

  // Aggregator scope. Templates carried this for powertrain; with no template step the user picks the
  // matrix itself from the inventory grid below.
  /**
   * Null while the first load is in flight, [] once it resolves empty (or fails). The distinction is what
   * separates "loading" from "nothing to show" - see the onFilterChanged override, which needs it because
   * a null rowData alone is NOT enough to keep ag-grid's loading overlay on screen here.
   *
   * <p>Signal-backed, exposed through a plain accessor so the template and tests read it unchanged. A signal
   * write schedules change detection through Angular's scheduler; assigning a plain field does not, it only
   * mutates and waits for something else to trigger a pass. On a full page load bootstrap produced enough
   * passes to hide that, but navigating to this page from elsewhere in the app leaves it quiescent - the rows
   * landed, nothing re-rendered, and the grid sat under its loading overlay until any event at all (a click
   * in the grid, or even the window losing focus) forced a pass. Same reason the matrix page's rowDataSignal
   * exists.
   */
  private readonly matrixInventorySignal = signal<AggregatorIoMatrixInventory[] | null>(null);

  get matrixInventory(): AggregatorIoMatrixInventory[] | null {
    return this.matrixInventorySignal();
  }

  set matrixInventory(rows: AggregatorIoMatrixInventory[] | null) {
    this.matrixInventorySignal.set(rows);
  }
  private readonly selectedMatrixSignal = signal<AggregatorIoMatrixInventory | null>(null);

  get selectedMatrix(): AggregatorIoMatrixInventory | null {
    return this.selectedMatrixSignal();
  }

  set selectedMatrix(value: AggregatorIoMatrixInventory | null) {
    this.selectedMatrixSignal.set(value);
  }

  /**
   * Shown while matrixInventory is null, in place of ag-grid's stock bare "Loading...", which reads like
   * the page is stuck rather than working.
   */
  readonly matrixInventoryLoadingOverlay =
    '<span class="ag-overlay-loading-center">Loading Aggregator IO Matrices…</span>';
  readonly programOptions = signal<SelectOption[]>([]);
  private readonly allowedPrograms = signal<AggregatorAllowedProgram[]>([]);

  /**
   * Reuses the base {@code selectedProgram} signal so this subclass does not redeclare
   * {@code selectedProgramSignal} (TS2415). Also copies the name onto ICD metadata for create.
   */
  override get selectedProgram(): string {
    return super.selectedProgram;
  }

  override set selectedProgram(value: string) {
    const program = value || '';
    super.selectedProgram = program;
    this.metadata = {...this.metadata, program};
  }

  /**
   * Blocking ICE/EV mismatch copy under Program. Empty when Type is unset or the axes match.
   */
  readonly programTypeMismatchWarning = computed(() => {
    if (this.matrixType() !== 'aggregator') {
      return '';
    }
    const matrixPowerType = this.selectedMatrixSignal()?.powerType;
    const programName = this.selectedProgram;
    if (!matrixPowerType || !programName) {
      return '';
    }
    const program = this.allowedPrograms().find(entry => entry.programName === programName);
    const programAxis = program?.resolvedPowerType;
    if (!programAxis || programAxis === matrixPowerType) {
      return '';
    }
    const display = program.programTypeDisplay || programAxis;
    return `Program Type (${display}) does not match the selected IO Matrix power type (${matrixPowerType}). Choose a matching program or one with Type unset.`;
  });
  readonly modelYearOptions: SelectOption[] = Array.from({length: 31},
    (_v, k) => ({label: String(2020 + k), value: String(2020 + k)}));

  /**
   * Aggregator matrices, shaped like the powertrain template inventory minus the ICD identity columns -
   * program, model year and RPO are not properties of a matrix, they are chosen per ICD on the form.
   * Pins reads "assigned/total" Signal Table pins - total is the ICD's pin count, every unassigned pin going
   * on as NC - so a half-finished matrix is obvious before an ICD is built from it.
   */
  readonly aggregatorInventoryCols: any[] = [
    {field: 'objectCode', headerName: 'Object Code', minWidth: 130},
    {field: 'architecture', headerName: 'Architecture', minWidth: 140},
    {field: 'powerType', headerName: 'Power Type', minWidth: 120},
    {field: 'keyword', headerName: 'Keyword', minWidth: 120},
    {field: 'cadenceVersion', headerName: 'Cadence', minWidth: 110,
      headerTooltip: 'Device IO cadence version this matrix is current against'},
    {field: 'pins', headerName: 'Pins', minWidth: 110,
      headerTooltip: 'Signal Table pins assigned a Device IO logical, out of all Signal Table pins (the ICD\'s pin count)',
      tooltipValueGetter: params => {
        const [assigned, total] = String(params?.value ?? '').split('/').map(Number);
        const nc = total - assigned;
        return `${assigned} of ${total} Signal Table pins are assigned to Device IO circuits.`
          + (nc > 0 ? ` The other ${nc} will be created on the ICD as NC.` : '');
      }},
    {field: 'updatedBy', headerName: 'Updated By', minWidth: 130},
    // Same formatting the powertrain template inventory applies to its audit dates, so the two flavours of
    // this page do not disagree about how a timestamp looks. The name in Updated By is resolved server-side.
    {field: 'updatedDate', headerName: 'Updated Date', minWidth: 160,
      valueFormatter: params => this.formatDateTime(params?.value)}
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this.applyLegacyMetadataDefaults();
    this.loadCreateIcdFormOptions();
    this.esciService.checkExportPermission(this);
    if (this.isAggregator) {
      this.loadAggregatorMatrixInventory();
      this.loadAggregatorPrograms();
    }
  }

  /** Aggregators have no template inventory to select from, so exports key off the matrix scope. */
  get isAggregator(): boolean {
    return this.matrixType() === 'aggregator';
  }

  /** The aggregator equivalent of having selected a template: one matrix picked from the grid. */
  get aggregatorScopeComplete(): boolean {
    return !!this.selectedMatrix;
  }

  get canStartIcdCreation(): boolean {
    return this.isAggregator ? this.aggregatorScopeComplete : this.selectedTemplateIds.length > 0;
  }

  /**
   * Whether the metadata form is live at all - the scope is picked and no create is in flight.
   *
   * Deliberately shares canStartIcdCreation's flavour-aware check rather than testing
   * selectedTemplateIds directly: an aggregator never has a selected template, so a template-count guard
   * here leaves Create permanently disabled with every required field filled in and no explanation.
   *
   * <p>This gates Cancel. Create additionally needs the required fields - see canCreateIcds.
   */
  get canSubmitCreateIcd(): boolean {
    return this.canStartIcdCreation && !this.createIcdSubmissionInProgress;
  }

  /**
   * The fields the form marks with a leading asterisk, in the order they appear on it so the
   * missing-field message reads top-to-bottom like the form the user is looking at.
   *
   * <p>Only "*All Users on ICD" is left out: that row carries no user, just an email-preference toggle
   * that always holds a value and an optional additional-emails box, so there is nothing to be blank.
   */
  private static readonly REQUIRED_METADATA_FIELDS: readonly RequiredMetadataField[] = [
    {field: 'icdType', label: 'ICD Type'},
    {field: 'icdSubType', label: 'ICD Sub-Type'},
    // Typed in on the aggregator form; powertrain derives all four from the selected template and shows
    // them readonly, so requiring them there would dead-end a user whose template is missing one.
    {field: 'program', label: 'Program', aggregatorOnly: true},
    {field: 'modelYear', label: 'Model Year', aggregatorOnly: true},
    {field: 'rpo', label: 'RPO', aggregatorOnly: true},
    {field: 'revision', label: 'ICDe Revision'},
    {field: 'deviceVariant', label: 'Device Variant'},
    {field: 'securityClassifier', label: 'Security Classifier'},
    // Readonly on both flavours, but still required: a scope that arrives without them would otherwise
    // create an ICD with no cadence or architecture at all.
    {field: 'cadence', label: 'Cadence'},
    {field: 'platform', label: 'Architecture'},
    {field: 'icdSourced', label: 'Is this Component Sourced?'},
    // The server only role-checks users that are present, and skips the check entirely when none are, so
    // nothing downstream stops a completely unassigned ICD. This is the only gate on it.
    {field: 'icdeEngineer', label: 'ICDe Engineer'},
    {field: 'deviceDre', label: 'Device DRE'},
    {field: 'electricalArchitect', label: 'Elec Architect'},
    {field: 'deviceEgm', label: 'Device EGM'},
    {field: 'architectEgm', label: 'Architect EGM'}
  ];

  /** Labels of every required field still blank, in form order. Empty when the form is complete. */
  get missingRequiredMetadataFields(): string[] {
    return CreateIcdsComponent.REQUIRED_METADATA_FIELDS
      .filter(required => !required.aggregatorOnly || this.isAggregator)
      // Whitespace-only counts as blank: the server trims these, so a space would pass here and then be
      // stored empty.
      .filter(required => !String(this.metadata?.[required.field] ?? '').trim())
      .map(required => required.label);
  }

  get createIcdMetadataComplete(): boolean {
    return this.missingRequiredMetadataFields.length === 0;
  }

  /** Shown next to the disabled Create button, and reused as the error if a create is attempted anyway. */
  get missingRequiredMetadataMessage(): string {
    return 'Fill in all required fields before creating ICDs: '
      + this.missingRequiredMetadataFields.join(', ') + '.';
  }

  /**
   * Gates Create only. Cancel stays on canSubmitCreateIcd on purpose - a user who opened the form and
   * left something blank still has to be able to back out of it.
   */
  get canCreateIcds(): boolean {
    return this.canSubmitCreateIcd && this.createIcdMetadataComplete && !this.programTypeMismatchWarning();
  }

  /**
   * One matrix at a time: an ICD is built from exactly one scope, and the grid is single-select so the
   * choice cannot be ambiguous. Copies the scope onto the metadata the create request reads from.
   */
  onAggregatorMatrixSelectionChanged(): void {
    const selected = (this.gridApi?.getSelectedRows?.() || []) as AggregatorIoMatrixInventory[];
    this.selectedMatrix = selected.length === 1 ? selected[0] : null;
    const architecture = this.selectedMatrix?.architecture || '';
    // The snapshot the ICD is built from, which is also what export and the create request use. The
    // matrix's own cadenceVersion is the Device IO source's newest, and the two differ whenever the
    // source has advanced past the last upgrade - showing that one would name a data set nobody built.
    const cadence = this.selectedMatrix?.savedCadenceVer || '';
    this.metadata = {
      ...this.metadata,
      objectCode: this.selectedMatrix?.objectCode || '',
      objectCodeInst: '',
      architecture,
      powerType: this.selectedMatrix?.powerType || '',
      keyword: this.selectedMatrix?.keyword || '',
      // Architecture and Cadence are required readonly fields on the form, so they are filled here rather
      // than left blank for the user to wonder about. platform cannot be defaulted in ngOnInit like the
      // others - no matrix is chosen yet at that point.
      platform: architecture,
      cadence,
      // Sent with the create request so the server uses this exact snapshot rather than re-resolving the
      // matrix's latest - which is a different set of rows if someone upgrades the matrix between this
      // selection and the submission.
      savedCadenceVer: cadence
    };
  }

  /**
   * Keeps the loading overlay up while the inventory request is still out.
   *
   * The base class shows the no-rows overlay whenever the displayed row count is 0, and onGridReady calls
   * it on its last line - after the grid has already put its loading overlay up for a null rowData. So the
   * loading overlay was being replaced by "no rows" immediately on creation, and the page read as empty for
   * the whole request rather than as loading. Once the request resolves, an empty result (including the
   * error path) falls through to the normal no-rows UX.
   */
  override onFilterChanged(): void {
    if (this.selectionInventoryLoading) {
      if (this.isGridApiAlive(this.gridApi)) {
        this.gridApi.showLoadingOverlay();
      }
      return;
    }
    super.onFilterChanged();
  }

  /**
   * Whether the selection grid's own data is still in flight. Each flavour reports it differently:
   * matrixInventory is null until it resolves, while templateInventory cannot be null (its setter coerces
   * to []) so powertrain carries an explicit flag.
   */
  private get selectionInventoryLoading(): boolean {
    return this.isAggregator ? this.matrixInventory === null : this.templateInventoryLoading;
  }

  /** The powertrain counterpart of matrixInventoryLoadingOverlay; the grid here lists ICD templates. */
  readonly templateInventoryLoadingOverlay =
    '<span class="ag-overlay-loading-center">Loading ICD Templates…</span>';

  private loadAggregatorMatrixInventory(): void {
    this.esciService.getAggregatorIoMatrixIcdMatrixInventory()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: rows => this.matrixInventory = rows || [],
        error: () => {
          // Land on an empty array, not back on null, or the grid sits under a loading overlay forever.
          this.matrixInventory = [];
          this.messageService.processError('Could not load the Aggregator IO Matrix list.');
        }
      });
  }

  private loadAggregatorPrograms(): void {
    this.esciService.getAggregatorIoMatrixIcdAllowedPrograms()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: programs => {
          this.allowedPrograms.set(programs);
          this.programOptions.set(this.toStringOptions(programs.map(program => program.programName)));
        },
        error: () => this.messageService.processError('Could not load programs.')
      });
  }

  protected override onCreateIcdMetadataReset(): void {
    this.selectedProgram = '';
    this.applyLegacyMetadataDefaults();
    this.dismissCreateIcdError();
  }

  private applyLegacyMetadataDefaults(): void {
    if (!this.metadata['icdType']) {
      this.metadata['icdType'] = 'Vehicle';
    }
    if (!this.metadata['icdSubType']) {
      this.metadata['icdSubType'] = 'NA';
    }
    if (!this.metadata['deviceVariant']) {
      this.metadata['deviceVariant'] = '01';
    }
    if (!this.metadata['revision']) {
      this.metadata['revision'] = '1';
    }
    if (!this.metadata['securityClassifier']) {
      this.metadata['securityClassifier'] = 'PROTECTED';
    }
    if (!this.metadata['icdSourced']) {
      this.metadata['icdSourced'] = 'N';
    }
    if (!this.metadata['platform']) {
      this.metadata['platform'] = this.metadata['architecture'] as string;
    }
    if (this.isAggregator) {
      // Always an EIO ICD - the checkbox is shown readonly so the user can see why the per-pin signal
      // fields are populated, not so they can turn it off.
      this.metadata['eioIcd'] = true;
    }
  }

  override startIcdCreation(): void {
    this.dismissCreateIcdError();
    if (!this.isAggregator && this.metadata['cadence'] === 'Multiple') {
      this.messageService.processError('Select templates from a single cadence before creating ICDs.');
      return;
    }
    this.refMapValidationCheckedSignal.set(false);
    this.refMapAvailableSignal.set(true);
    if (this.isAggregator) {
      // There are no templates to select or to derive metadata from; the scope pickers stand in for both.
      if (!this.aggregatorScopeComplete) {
        this.messageService.processError('Select an Aggregator IO Matrix first.');
        return;
      }
      this.dismissCreateIcdSuccess();
      this.createIcdSubmissionInProgress = false;
      this.icdCreationStarted = true;
      this.metadata = {...this.metadata, objectCodeInst: ''};
    } else {
      super.startIcdCreation();
      if (!this.icdCreationStarted) {
        return;
      }
    }
    this.loadAndApplyReferenceMappingForSelectedTemplates();
  }

  /** For aggregators this is the scope-selection step; there is no template grid to show. */
  get showTemplateSelectionStep(): boolean {
    return !this.icdCreationStarted || this.createIcdSuccessVisible;
  }

  get showCreateIcdMetadataForm(): boolean {
    return this.icdCreationStarted &&
      this.refMapValidationCheckedSignal() &&
      this.refMapAvailableSignal() &&
      !this.createIcdSuccessVisible &&
      // Hidden while the create runs: an aggregator ICD is built from every row of the matrix and can take
      // a while, and a still-editable form behind a spinner invites edits that cannot affect the request.
      !this.createIcdSubmissionInProgress;
  }

  get showCreateIcdActionButtons(): boolean {
    return this.showCreateIcdMetadataForm;
  }

  get refMapBlockMessage(): string {
    return 'Reference Mapping must be created for this Object Code/Object Code Inst (or Controller Designator) before creating ICDs from this template.';
  }

  /**
   * Aggregator reports never fall back to the client-side grid export the base class uses when there is no
   * server request: this page's grid is the list of matrices, so exporting it would hand back a list of
   * scopes where the user asked for the pins in one of them.
   */
  override exportReport(): void {
    if (this.isAggregator && !this.selectedMatrix) {
      this.messageService.processError('Select an Aggregator IO Matrix first.');
      return;
    }
    super.exportReport();
  }

  protected override getReportDownloadRequest(): Observable<ArrayBuffer> | null {
    if (this.matrixType() === 'aggregator') {
      // Non-null for aggregator whenever exportReport let us get here, so the grid fallback never runs.
      return this.esciService.getAggregatorIoMatrixReport(
        this.selectedMatrix!.objectCode,
        this.selectedMatrix!.architecture,
        this.selectedMatrix!.powerType,
        this.selectedMatrix!.savedCadenceVer,
        this.selectedMatrix!.keyword || '');
    }
    return this.esciService.getIoMatrixIcdTemplateReport(this.selectedTemplateIds || []);
  }

  protected override getHistoryDownloadRequest(): Observable<ArrayBuffer> | null {
    if (this.matrixType() === 'aggregator') {
      return this.esciService.getAggregatorIoMatrixIcdHistoryForScope(
        String(this.metadata['objectCode'] || ''),
        String(this.metadata['architecture'] || ''),
        String(this.metadata['powerType'] || ''),
        String(this.metadata['keyword'] || ''));
    }
    return this.esciService.getIoMatrixIcdTemplateHistory(this.selectedTemplateIds || []);
  }

  protected override getReportFileNamePrefix(): string {
    return this.matrixType() === 'aggregator' ? 'AggregatorIoMatrixReport' : 'IoMatrixIcdTemplateReport';
  }

  protected override getHistoryFileNamePrefix(): string {
    return this.matrixType() === 'aggregator' ? 'AggregatorIoMatrixHistory' : 'IoMatrixIcdTemplateHistory';
  }


  override createIcds(): void {
    if (!this.refMapAvailableSignal()) {
      this.setCreateIcdError(this.refMapBlockMessage);
      return;
    }
    // Belt and braces behind the disabled button: this also covers a stale button state and the
    // powertrain flavour, where nothing server-side requires the user roles.
    if (!this.createIcdMetadataComplete) {
      this.setCreateIcdError(this.missingRequiredMetadataMessage);
      return;
    }
    if (this.programTypeMismatchWarning()) {
      this.setCreateIcdError(this.programTypeMismatchWarning());
      return;
    }
    this.dismissCreateIcdError();
    super.createIcds();
  }

  dismissCreateIcdError(): void {
    this.createIcdErrorVisible = false;
    this.createIcdErrorMessage = '';
  }

  protected override handleCreateIcdError(message: string): void {
    this.setCreateIcdError(message);
  }

  private setCreateIcdError(message: string): void {
    this.dismissCreateIcdSuccess();
    this.createIcdErrorMessage = String(message || 'Failed to create ICDs.');
    this.createIcdErrorVisible = true;
  }

  private loadAndApplyReferenceMappingForSelectedTemplates(): void {
    const objectCode = String(this.metadata['objectCode'] || '').trim();
    const objectCodeInst = String(this.metadata['objectCodeInst'] || '').trim();
    if (!objectCode) {
      this.handleMissingRefMappingDuringCreate();
      return;
    }
    // Must follow the active flavour: the powertrain endpoint gates on POWERTRAIN_IO_MATRIX_ICD_USER,
    // so an AGG_IO_MATRIX_ICD_USER-only user would get a 401 here and be blocked from creating
    // aggregator ICDs entirely (the error path aborts the flow).
    const refMapRequest = this.matrixType() === 'aggregator'
      ? this.esciService.getAggregatorIoMatrixIcdRefMapStatus(objectCode, objectCodeInst)
      : this.esciService.getIcdRefMapStatus(objectCode, objectCodeInst);
    refMapRequest.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (status: IcdRefMapStatus | null) => {
        // null/empty status is a normal "no mapping configured" state. The closable
        // inline createIcdError below explains it; we do NOT toast here.
        const hasMapping = !!(status && (status as any).id != null && Number((status as any).id) > 0);
        this.refMapAvailableSignal.set(hasMapping);
        this.refMapValidationCheckedSignal.set(true);
        if (!hasMapping) {
          // Abort the ICD-creation flow so the template selection grid stays visible
          // (instead of stranding the user behind a banner with no grid and no form).
          // The ref-map block message is surfaced through the closable createIcdError UI.
          this.handleMissingRefMappingDuringCreate();
          return;
        }
        this.metadata = {
          ...this.metadata,
          objectCode: status.objectCode?.code || objectCode,
          // Keep the selected scope OCI. Mapping.inst is often a stored space for blank,
          // and a wrong first-match row (e.g. SECOND for B9) must not overwrite blank.
          objectCodeInst,
          vpps: status.vpps?.vpps || '',
          upc: status.upc?.upcCode || '',
          fna: status.fna?.fnaCode || ''
        };
      },
      error: (err) => {
        // Distinguish "request failed" from the "missing data" path above with an explicit toast.
        this.refMapAvailableSignal.set(false);
        this.refMapValidationCheckedSignal.set(true);
        this.messageService.processError(
          err?.error?.message
            || 'Could not load Reference Mapping status (request failed). Try again or contact support.');
      }
    });
  }

  private handleMissingRefMappingDuringCreate(): void {
    this.refMapAvailableSignal.set(false);
    this.refMapValidationCheckedSignal.set(true);
    this.icdCreationStarted = false;
    this.setCreateIcdError(this.refMapBlockMessage);
  }

  private loadCreateIcdFormOptions(): void {
    const formOptionsRequest = this.matrixType() === 'aggregator'
      ? this.esciService.getAggregatorIoMatrixIcdCreateIcdFormOptions()
      : this.esciService.getCreateIcdFormOptions();
    formOptionsRequest.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: CreateIcdFormOptionsResponse) => {
        this.revisionOptions = this.toStringOptions(response?.revisionOptions, false, this.buildRevisionFallbackOptions());
        this.deviceVariantOptions = this.toStringOptions(response?.deviceVariantOptions, false, this.buildDeviceVariantFallbackOptions());
        this.buildStatusOptions = this.toStringOptions(response?.buildStatusOptions, true);
        this.securityClassifierOptions = this.toSelectOptions(response?.securityClassifierOptions, this.securityClassifierOptions);
        this.componentSourcedOptions = this.toSelectOptions(response?.componentSourcedOptions, this.componentSourcedOptions);
        this.operateNormallyOptions = this.toSelectOptions(response?.operateNormallyOptions, this.operateNormallyOptions, true);
        this.fullyFunctionalOptions = this.toSelectOptions(response?.fullyFunctionalOptions, this.fullyFunctionalOptions, true);
      },
      error: () => {
        this.revisionOptions = this.buildRevisionFallbackOptions();
        this.deviceVariantOptions = this.buildDeviceVariantFallbackOptions();
        this.buildStatusOptions = this.toStringOptions([], true);
        this.operateNormallyOptions = this.withBlankOption(this.operateNormallyOptions);
        this.fullyFunctionalOptions = this.withBlankOption(this.fullyFunctionalOptions);
      }
    });
  }

  private toStringOptions(
    values: string[] | null | undefined,
    includeBlank = false,
    fallbackOptions: SelectOption[] = []
  ): SelectOption[] {
    const mapped = (values || [])
      .map((value) => String(value || '').trim())
      .filter((value) => value.length > 0)
      .map((value) => ({label: value, value}));
    const resolved = mapped.length > 0 ? mapped : fallbackOptions;
    return includeBlank ? this.withBlankOption(resolved) : resolved;
  }

  private toSelectOptions(options: IcdSelectOption[] | null | undefined, fallback: SelectOption[], includeBlank = false): SelectOption[] {
    const mapped = (options || [])
      .map((option) => ({
        label: String(option?.label || '').trim(),
        value: String(option?.value || '').trim()
      }))
      .filter((option) => option.label && option.value);
    const resolved = mapped.length > 0 ? mapped : fallback;
    return includeBlank ? this.withBlankOption(resolved) : resolved;
  }

  private withBlankOption(options: SelectOption[]): SelectOption[] {
    const normalized = options || [];
    if (normalized.some((option) => option.value === '')) {
      return normalized;
    }
    return [{label: '', value: ''}, ...normalized];
  }

  private buildRevisionFallbackOptions(): SelectOption[] {
    const fallback: SelectOption[] = [];
    for (let revision = 1; revision < 1000; revision++) {
      const value = String(revision);
      fallback.push({label: value, value});
    }
    return fallback;
  }

  private buildDeviceVariantFallbackOptions(): SelectOption[] {
    const fallback: SelectOption[] = [];
    for (let variant = 1; variant < 100; variant++) {
      const value = variant < 10 ? `0${variant}` : String(variant);
      fallback.push({label: value, value});
    }
    return fallback;
  }
}

